import React, { useState } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../firebase/AuthContext';
import styled from 'styled-components';

// List of authorized admin emails - add your email here
const AUTHORIZED_ADMIN_EMAILS = [
  'topshotrewards@gmail.com'
  // You can add more emails if needed in the future
];

const Container = styled.div`
  max-width: 600px;
  margin: 2rem auto;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #C41E3A;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #A01930;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 4px;
  background-color: ${props => props.success ? '#d1e7dd' : '#f8d7da'};
  color: ${props => props.success ? '#0f5132' : '#842029'};
`;

const UserInfo = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  
  p {
    margin: 0.5rem 0;
  }
  
  strong {
    color: #333;
  }
`;

const SetupAdmin = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Check if current user is already an admin
  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) return;
      
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists() && userDoc.data().isAdmin) {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };
    
    checkAdminStatus();
  }, [currentUser]);
  
  const isAuthorizedEmail = (email) => {
    return AUTHORIZED_ADMIN_EMAILS.includes(email.toLowerCase());
  };
  
  const makeAdmin = async () => {
    if (!currentUser) {
      setStatus({
        success: false,
        message: 'You must be logged in to perform this action.'
      });
      return;
    }
    
    // Check if email is authorized
    if (!isAuthorizedEmail(currentUser.email)) {
      setStatus({
        success: false,
        message: 'Your account is not authorized to become an admin.'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Check if user document exists
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // Update existing document
        await setDoc(userRef, { 
          ...userDoc.data(),
          isAdmin: true,
          updatedAt: new Date()
        });
      } else {
        // Create new document
        await setDoc(userRef, {
          email: currentUser.email,
          displayName: currentUser.displayName || '',
          photoURL: currentUser.photoURL || '',
          isAdmin: true,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      
      setIsAdmin(true);
      setStatus({
        success: true,
        message: 'You are now an admin! You can access the admin dashboard.'
      });
    } catch (error) {
      console.error('Error setting admin status:', error);
      setStatus({
        success: false,
        message: `Error: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };
  
  if (!currentUser) {
    return (
      <Container>
        <Title>Admin Setup</Title>
        <Description>
          You must be logged in to set up admin access.
        </Description>
      </Container>
    );
  }
  
  // Check if current user's email is authorized
  const isEmailAuthorized = isAuthorizedEmail(currentUser.email);
  
  return (
    <Container>
      <Title>Admin Setup</Title>
      <Description>
        This utility allows authorized users to grant admin privileges to their account.
        Admin users can access the admin dashboard and manage products, orders, and more.
      </Description>
      
      <UserInfo>
        <p><strong>Email:</strong> {currentUser.email}</p>
        <p><strong>User ID:</strong> {currentUser.uid}</p>
        <p><strong>Admin Status:</strong> {isAdmin ? 'Admin' : 'Not Admin'}</p>
        <p><strong>Authorization:</strong> {isEmailAuthorized ? 'Authorized' : 'Not Authorized'}</p>
      </UserInfo>
      
      {!isAdmin && isEmailAuthorized && (
        <Button 
          onClick={makeAdmin} 
          disabled={loading}
        >
          {loading ? 'Processing...' : 'Make Me Admin'}
        </Button>
      )}
      
      {!isAdmin && !isEmailAuthorized && (
        <StatusMessage success={false}>
          Your email address is not authorized to become an admin.
        </StatusMessage>
      )}
      
      {isAdmin && (
        <Button onClick={() => window.location.href = '/admin'}>
          Go to Admin Dashboard
        </Button>
      )}
      
      {status && (
        <StatusMessage success={status.success}>
          {status.message}
        </StatusMessage>
      )}
    </Container>
  );
};

export default SetupAdmin;
